import type { ReactNode } from "react";
import { seoDefaults } from "./seoConfig";

export const siteUrl = seoDefaults.baseUrl;

export const siteName = seoDefaults.siteName;

export const defaultMeta = {
  title: seoDefaults.defaultTitle,
  description: seoDefaults.defaultDescription,
  image: seoDefaults.defaultOGImage,
};

export type JsonLd = Record<string, unknown>;

export interface SeoConfig {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  /** Only include these query params in canonical URLs to avoid duplicates */
  preserveQueryParams?: string[];
  structuredData?: JsonLd | JsonLd[];
  children?: ReactNode;
}

export interface ResolvedSeoConfig extends SeoConfig {
  canonicalUrl: string;
  ogImage: string;
  robots: string;
  title: string;
  description: string;
}

export const organizationJsonLd: JsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: siteName,
  url: siteUrl,
  logo: `${siteUrl}/logo.png`,
  sameAs: [
    "https://www.facebook.com/TravelWingsGlobal",
    "https://www.instagram.com/TravelWingsGlobal",
    "https://www.linkedin.com/company/travel-wings-global",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+91-88826-40963",
      contactType: "customer service",
      areaServed: "IN",
      availableLanguage: ["en"],
    },
  ],
};

export const websiteJsonLd: JsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: siteUrl,
  name: siteName,
  potentialAction: {
    "@type": "SearchAction",
    target: `${siteUrl}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function buildProductJsonLd(config: {
  name: string;
  url: string;
  description?: string;
  image?: string;
  price?: number;
  priceCurrency?: string;
  availability?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: config.name,
    description: config.description,
    image: config.image,
    url: config.url,
    ...(config.price
      ? {
          offers: {
            "@type": "Offer",
            price: config.price,
            priceCurrency: config.priceCurrency || "INR",
            availability: config.availability || "https://schema.org/InStock",
            url: config.url,
          },
        }
      : {}),
  };
}

export function resolveAbsoluteUrl(path?: string) {
  if (!path) return siteUrl;
  if (path.startsWith("http")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${siteUrl}${normalized}`;
}

export function buildCanonicalUrl(
  path?: string,
  options?: { preserveQueryParams?: string[] }
) {
  if (!path) return siteUrl;

  const [pathname, queryString] = path.split("?");
  const canonicalBase = resolveAbsoluteUrl(pathname);

  if (!queryString || !options?.preserveQueryParams?.length) {
    return canonicalBase;
  }

  const query = new URLSearchParams(queryString);
  const preserved = new URLSearchParams();

  options.preserveQueryParams.forEach((param) => {
    const value = query.get(param);
    if (value !== null) preserved.set(param, value);
  });

  const preservedString = preserved.toString();
  return preservedString
    ? `${canonicalBase}?${preservedString}`
    : canonicalBase;
}

export function buildBreadcrumbJsonLd(
  items: Array<{ name: string; url: string }>
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function buildWebPageJsonLd(config: {
  name: string;
  description?: string;
  url: string;
  image?: string;
  type?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": config.type || "WebPage",
    name: config.name,
    description: config.description,
    url: config.url,
    image: config.image,
    inLanguage: "en",
    isPartOf: {
      "@type": "WebSite",
      url: siteUrl,
      name: siteName,
    },
  };
}

export function buildTouristTripJsonLd(config: {
  name: string;
  url: string;
  description?: string;
  image?: string;
  destination?: string;
  tripType?: string;
  highlights?: string[];
  price?: number;
  priceCurrency?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: config.name,
    description: config.description,
    image: config.image,
    url: config.url,
    ...(config.tripType ? { touristType: config.tripType } : {}),
    ...(config.destination
      ? {
          destination: {
            "@type": "TouristDestination",
            name: config.destination,
          },
        }
      : {}),
    ...(config.highlights?.length
      ? {
          itinerary: config.highlights.map((highlight, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "TouristAttraction",
              name: highlight,
            },
          })),
        }
      : {}),
    ...(config.price
      ? {
          offers: {
            "@type": "Offer",
            price: config.price,
            priceCurrency: config.priceCurrency || "INR",
            availability: "https://schema.org/InStock",
            url: config.url,
          },
        }
      : {}),
  };
}

export function buildOgImageUrl(image?: string) {
  if (!image) return seoDefaults.defaultOGImage;
  if (image.startsWith("http")) return image;
  const normalized = image.startsWith("/") ? image : `/${image}`;
  return `${siteUrl}${normalized}`;
}

export function buildRobotsContent(noindex?: boolean) {
  return noindex ? "noindex, nofollow" : seoDefaults.defaultRobots;
}

export function buildSeoMeta(config: SeoConfig = {}): ResolvedSeoConfig {
  const title = config.title || defaultMeta.title;
  const description = config.description || defaultMeta.description;
  const canonicalUrl = buildCanonicalUrl(config.path, {
    preserveQueryParams: config.preserveQueryParams,
  });

  return {
    ...config,
    title,
    description,
    canonicalUrl,
    ogImage: buildOgImageUrl(config.image),
    robots: buildRobotsContent(config.noindex),
  };
}
